import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { BarChart3, Download, TrendingUp, Calendar, Filter } from "lucide-react"

export default function Relatorios() {
  const relatorios = [
    {
      id: 1,
      nome: "Vendas por Mês",
      descricao: "Análise mensal de vendas e conversões",
      categoria: "Vendas",
      ultimaExecucao: "2024-01-18",
      formato: "PDF"
    },
    {
      id: 2,
      nome: "Performance de Instalações",
      descricao: "Relatório de eficiência das instalações",
      categoria: "Técnico",
      ultimaExecucao: "2024-01-17",
      formato: "Excel"
    },
    {
      id: 3,
      nome: "ROI por Cliente",
      descricao: "Análise de retorno sobre investimento",
      categoria: "Financeiro",
      ultimaExecucao: "2024-01-15",
      formato: "PDF"
    },
    {
      id: 4,
      nome: "Leads por Origem",
      descricao: "Análise de canais de aquisição",
      categoria: "Marketing",
      ultimaExecucao: "2024-01-16",
      formato: "Excel"
    }
  ]

  const getCategoryColor = (categoria: string) => {
    switch (categoria) {
      case "Vendas": return "default"
      case "Técnico": return "secondary"
      case "Financeiro": return "outline"
      case "Marketing": return "destructive"
      default: return "secondary"
    }
  }

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Relatórios</h1>
          <p className="text-muted-foreground">Análises e insights do negócio solar</p>
        </div>
        <Button className="gap-2">
          <TrendingUp className="h-4 w-4" />
          Novo Relatório
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Relatórios Disponíveis</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{relatorios.length}</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Executados Hoje</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-success">3</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Automáticos</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-warning">2</div>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium">Sob Demanda</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{relatorios.length - 2}</div>
          </CardContent>
        </Card>
      </div>

      <div className="flex gap-4">
        <Button variant="outline" className="gap-2">
          <Filter className="h-4 w-4" />
          Filtrar por Categoria
        </Button>
        <Button variant="outline" className="gap-2">
          <Calendar className="h-4 w-4" />
          Período
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Relatórios Disponíveis
          </CardTitle>
          <CardDescription>
            Análises e métricas do seu negócio solar
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {relatorios.map((relatorio) => (
              <div key={relatorio.id} className="flex items-center justify-between p-4 border border-border rounded-lg">
                <div className="space-y-1">
                  <h3 className="font-semibold">{relatorio.nome}</h3>
                  <p className="text-sm text-muted-foreground">{relatorio.descricao}</p>
                  <div className="flex gap-2 items-center text-sm">
                    <Badge variant={getCategoryColor(relatorio.categoria)}>
                      {relatorio.categoria}
                    </Badge>
                    <span className="text-muted-foreground">
                      Última execução: {new Date(relatorio.ultimaExecucao).toLocaleDateString('pt-BR')}
                    </span>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{relatorio.formato}</Badge>
                  <Button variant="outline" size="sm" className="gap-1">
                    <Download className="h-3 w-3" />
                    Gerar
                  </Button>
                  <Button size="sm">
                    Executar
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Insights Rápidos</CardTitle>
          <CardDescription>
            Métricas importantes em tempo real
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="p-4 border border-border rounded-lg">
              <h4 className="font-semibold text-success">Taxa de Conversão</h4>
              <p className="text-2xl font-bold">24.5%</p>
              <p className="text-sm text-muted-foreground">+2.3% vs mês anterior</p>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <h4 className="font-semibold text-warning">Tempo Médio de Instalação</h4>
              <p className="text-2xl font-bold">18 dias</p>
              <p className="text-sm text-muted-foreground">-3 dias vs mês anterior</p>
            </div>
            <div className="p-4 border border-border rounded-lg">
              <h4 className="font-semibold text-info">Satisfação do Cliente</h4>
              <p className="text-2xl font-bold">4.8/5.0</p>
              <p className="text-sm text-muted-foreground">Baseado em 127 avaliações</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
